import { useState } from "react";
import {
  HANDBOOK_SECTIONS, HANDBOOK_VERSION, HANDBOOK_EFFECTIVE_DATE, HANDBOOK_DISCLAIMER,
} from "@/constants/handbook";
import { Navigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Brand";

type Step = 0 | 1 | 2 | 3 | 4;

export function Onboarding() {
  const { employee, refreshEmployee } = useAuth();
  const [step, setStep] = useState<Step>(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 1: Personal
  const [first, setFirst] = useState(employee?.first_name ?? "");
  const [last, setLast] = useState(employee?.last_name ?? "");
  const [pref, setPref] = useState(employee?.preferred_name ?? "");
  const [pronouns, setPronouns] = useState(employee?.preferred_pronouns ?? "");
  const [dob, setDob] = useState(employee?.date_of_birth ?? "");

  // Step 2: Contact
  const [phone, setPhone] = useState(employee?.phone ?? "");
  const [address, setAddress] = useState(employee?.address ?? "");
  const [city, setCity] = useState(employee?.city ?? "");
  const [stateAbbr, setStateAbbr] = useState(employee?.state ?? "");
  const [zip, setZip] = useState(employee?.zip_code ?? "");

  // Step 3: Emergency
  const [emName, setEmName] = useState("");
  const [emRel, setEmRel] = useState("");
  const [emPhone, setEmPhone] = useState("");

  // Step 4: Handbook
  const [ack, setAck] = useState(false);

  if (!employee) return <Navigate to="/login" replace />;
  if (employee.onboarding_completed_at) return <Navigate to="/" replace />;

  async function savePersonal() {
    setBusy(true); setError(null);
    const { error } = await supabase.from("hq_employees_people").update({
      first_name: first, last_name: last, preferred_name: pref || null,
      preferred_pronouns: pronouns || null, date_of_birth: dob || null,
    }).eq("id", employee!.id);
    setBusy(false);
    if (error) setError(error.message); else { await refreshEmployee(); setStep(1); }
  }

  async function saveContact() {
    setBusy(true); setError(null);
    const { error } = await supabase.from("hq_employees_people").update({
      phone, address, city, state: stateAbbr, zip_code: zip,
    }).eq("id", employee!.id);
    setBusy(false);
    if (error) setError(error.message); else { await refreshEmployee(); setStep(2); }
  }

  async function saveEmergency() {
    setBusy(true); setError(null);
    const { error } = await supabase.from("hq_emergency_contacts").insert({
      employee_id: employee!.id,
      name: emName, relationship: emRel || null, phone: emPhone || null,
      is_primary: true, sort_order: 0,
    });
    setBusy(false);
    if (error) setError(error.message); else setStep(3);
  }

  async function complete() {
    setBusy(true); setError(null);
    const { error } = await supabase.from("hq_employees_people").update({
      onboarding_completed_at: new Date().toISOString(),
      handbook_acknowledged_at: new Date().toISOString(),
    }).eq("id", employee!.id);
    setBusy(false);
    if (error) setError(error.message); else { await refreshEmployee(); setStep(4); }
  }

  const STEPS = ["Personal", "Contact", "Emergency", "Handbook", "Done"];

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-offwhite via-white to-teal-mint/30 p-4">
      <div className="w-full max-w-xl">
        <div className="mb-6 flex justify-center"><Brand /></div>

        {/* Stepper */}
        <div className="mb-4 flex items-center gap-2">
          {STEPS.map((label, i) => (
            <div key={label} className="flex-1">
              <div className={`mx-auto h-1.5 rounded-full ${i <= step ? "bg-teal-dark" : "bg-line"}`} />
              <div className={`mt-1 text-center text-[10px] uppercase tracking-wider ${i === step ? "text-teal-dark font-semibold" : "text-gray2"}`}>{label}</div>
            </div>
          ))}
        </div>

        <div className="card shadow-soft">
          {error && <div className="mb-3 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">{error}</div>}

          {step === 0 && (
            <>
              <h1 className="mb-1 text-2xl font-bold">Welcome to Scenta HQ 👋</h1>
              <p className="mb-5 text-sm text-gray2">Let's get your account set up. Should take ~2 minutes.</p>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="label">Legal first name *</label><input className="input" required value={first} onChange={e => setFirst(e.target.value)} autoFocus /></div>
                  <div><label className="label">Legal last name *</label><input className="input" required value={last} onChange={e => setLast(e.target.value)} /></div>
                </div>
                <div><label className="label">Preferred name (optional)</label><input className="input" value={pref} onChange={e => setPref(e.target.value)} placeholder="What do you go by?" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="label">Pronouns</label><input className="input" value={pronouns} onChange={e => setPronouns(e.target.value)} placeholder="she/her, they/them…" /></div>
                  <div><label className="label">Date of birth *</label><input type="date" required className="input" value={dob} onChange={e => setDob(e.target.value)} /></div>
                </div>
              </div>
              <div className="mt-6 flex justify-end">
                <button onClick={savePersonal} disabled={busy || !first || !last || !dob} className="btn">Continue →</button>
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <h1 className="mb-1 text-2xl font-bold">How can we reach you?</h1>
              <p className="mb-5 text-sm text-gray2">Cell + mailing address are required for payroll.</p>
              <div className="space-y-3">
                <div><label className="label">Cell phone *</label><input className="input" required value={phone} onChange={e => setPhone(e.target.value)} placeholder="(555) 123-4567" /></div>
                <div><label className="label">Mailing address *</label><input className="input" required value={address} onChange={e => setAddress(e.target.value)} placeholder="Street address" /></div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2"><label className="label">City *</label><input className="input" required value={city} onChange={e => setCity(e.target.value)} /></div>
                  <div><label className="label">State *</label><input className="input" required value={stateAbbr} onChange={e => setStateAbbr(e.target.value.toUpperCase())} maxLength={2} /></div>
                </div>
                <div><label className="label">Zip *</label><input className="input" required value={zip} onChange={e => setZip(e.target.value)} /></div>
              </div>
              <div className="mt-6 flex justify-between">
                <button onClick={() => setStep(0)} className="btn btn-ghost">← Back</button>
                <button onClick={saveContact} disabled={busy || !phone || !address || !city || !stateAbbr || !zip} className="btn">Continue →</button>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <h1 className="mb-1 text-2xl font-bold">Emergency contact</h1>
              <p className="mb-5 text-sm text-gray2">Who should we call if there's ever an emergency? You can add more later.</p>
              <div className="space-y-3">
                <div><label className="label">Name *</label><input className="input" required value={emName} onChange={e => setEmName(e.target.value)} autoFocus /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="label">Relationship *</label><input className="input" required value={emRel} onChange={e => setEmRel(e.target.value)} placeholder="Spouse, parent…" /></div>
                  <div><label className="label">Phone *</label><input className="input" required value={emPhone} onChange={e => setEmPhone(e.target.value)} /></div>
                </div>
              </div>
              <div className="mt-6 flex justify-between">
                <button onClick={() => setStep(1)} className="btn btn-ghost">← Back</button>
                <button onClick={saveEmergency} disabled={busy || !emName || !emRel || !emPhone} className="btn">Continue →</button>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <h1 className="mb-1 text-2xl font-bold">Employee Handbook</h1>
              <p className="mb-3 text-sm text-gray2">Please read and acknowledge below. Version {HANDBOOK_VERSION} · Effective {HANDBOOK_EFFECTIVE_DATE}.</p>
              <div className="mb-4 max-h-96 space-y-5 overflow-y-auto rounded-lg bg-offwhite p-4 text-sm leading-relaxed">
                {HANDBOOK_SECTIONS.map(s => (
                  <section key={s.id}>
                    <h3 className="mb-1 font-bold">{s.title}</h3>
                    <div className="whitespace-pre-wrap text-gray2">{s.body}</div>
                  </section>
                ))}
                <p className="border-t border-line pt-3 text-xs italic text-gray2">{HANDBOOK_DISCLAIMER}</p>
              </div>
              <label className="flex items-start gap-2 cursor-pointer rounded-lg ring-1 ring-line p-3 hover:ring-teal">
                <input type="checkbox" checked={ack} onChange={e => setAck(e.target.checked)} className="mt-0.5" />
                <span className="text-sm">I have read and agree to abide by the Scenta Employee Handbook.</span>
              </label>
              <div className="mt-6 flex justify-between">
                <button onClick={() => setStep(2)} className="btn btn-ghost">← Back</button>
                <button onClick={complete} disabled={busy || !ack} className="btn">Complete Setup ✓</button>
              </div>
            </>
          )}

          {step === 4 && (
            <div className="py-8 text-center">
              <div className="mb-2 text-5xl">🎉</div>
              <h1 className="text-2xl font-bold">You're all set!</h1>
              <p className="mt-1 mb-6 text-sm text-gray2">Banking, tax docs, and ID can be filled in anytime under My Profile.</p>
              <a href="/" className="btn btn-lg">Go to my dashboard →</a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
